import React from 'react';
import { useNavigate } from 'react-router-dom';

export const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-8 relative overflow-hidden font-sans bg-zinc-950">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        @keyframes borderGlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes ghostWobble {
          0%, 100% { transform: rotate(-6deg); }
          50% { transform: rotate(6deg); }
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @keyframes digitPop {
          0% { opacity: 0; transform: translateY(20px) scale(0.8); }
          60% { opacity: 1; transform: translateY(-4px) scale(1.05); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .ambient-glow { animation: pulseGlow 8s ease-in-out infinite; }
        .float-y { animation: floatY 4.5s ease-in-out infinite; }
        .ghost-wobble { animation: ghostWobble 3s ease-in-out infinite; }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.4) 0%, rgba(24, 24, 27, 0.8) 50%, rgba(20, 184, 166, 0.4) 100%);
          background-size: 200% 200%;
          animation: borderGlow 6s ease infinite;
        }

        .micro-interaction { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .shimmer-text {
          background: linear-gradient(90deg, #52525b 0%, #10b981 50%, #52525b 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 5s linear infinite;
        }

        .digit-pop {
          display: inline-block;
          animation: digitPop 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
        .digit-pop:nth-child(1) { animation-delay: 0.1s; }
        .digit-pop:nth-child(2) { animation-delay: 0.25s; }
        .digit-pop:nth-child(3) { animation-delay: 0.4s; }
      `}</style>

      {/* Ambient background blur circles */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[140px] pointer-events-none ambient-glow" />

      <div className="relative z-10 max-w-lg w-full">
        {/* Card with animated gradient border */}
        <div className="relative p-[1px] rounded-[2.5rem] highlight-border shadow-2xl animate-spring-in">
          <div className="bg-zinc-900/95 backdrop-blur-xl p-8 sm:p-10 rounded-[2.45rem] text-center space-y-6 relative overflow-hidden">

            {/* Wobbling ghost icon */}
            <div className="relative w-20 h-20 mx-auto float-y">
              <div className="relative w-20 h-20 rounded-full bg-zinc-950 border border-emerald-500/30 flex items-center justify-center shadow-lg shadow-emerald-500/10 ghost-wobble">
                <svg className="w-9 h-9 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* 404 digits with pop-in */}
            <div className="text-6xl sm:text-7xl font-black tracking-tight text-emerald-400 leading-none">
              <span className="digit-pop">4</span>
              <span className="digit-pop">0</span>
              <span className="digit-pop">4</span>
            </div>

            {/* Badge */}
            <span className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-500/10">
              Page Not Found
            </span>

            {/* Heading & description */}
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-100">
                Lost in the Void
              </h1>
              <p className="text-zinc-400 text-sm font-medium leading-relaxed max-w-sm mx-auto">
                The page you're looking for seems to have <span className="shimmer-text font-bold">wandered off</span>. It may have been moved, renamed, or never existed.
              </p>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-700/60 to-transparent" />

            {/* Status info */}
            <div className="flex items-center justify-center gap-6 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
                Error 404
              </span>
              <span className="w-1 h-1 rounded-full bg-zinc-700" />
              <span>Invalid Route</span>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                </svg>
                Go Back
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-zinc-950 hover:bg-zinc-800 text-zinc-300 hover:text-zinc-100 rounded-xl text-xs font-bold border border-zinc-800 transition-all micro-interaction active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Home
              </button>
            </div>

          </div>
        </div>

        {/* Footnote */}
        <p className="text-center text-[10px] font-bold uppercase tracking-widest text-zinc-600 mt-6 animate-spring-in">
          Check the URL or return to a known page
        </p>
      </div>
    </div>
  );
};

export default NotFoundPage;